import "./zoom-gallery";

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.activeVideos = [];
    this._isSyncing = false;
    this._thumbnailNavIndex = 0;
    this._pendingMediaId = null;
    this._pendingFallbackToFirst = false;

    /*
     * IMPORTANT:
     *
     * Do not start the old per-slide skeleton/pending lifecycle.
     *
     * During AJAX PDP navigation the critical images have already
     * been warmed/preloaded before this gallery enters the DOM.
     *
     * The previous calls:
     *
     * this._initMediaSlideSkeletons();
     * this._initSequentialThumbnailReveal();
     *
     * created a second visual loading cycle after morph.
     */

    window.loadSwiper.then((swiper) => {
      this.swiperLib = swiper;

      if (!this._isGiftCard) {
        this._initThumbsCarousel();
      }

      this._initCarousel();

      if (!this._isGiftCard) {
        this._appendResolvedLifestyleSlides();
        this._initZoomButtons();
        this._syncCarousels();
      }

      const pendingIndex = this._pendingMediaId != null ? this._getMediaIndexById(this._pendingMediaId) : -1;

      const pendingFallbackToFirst = this._pendingFallbackToFirst;

      this._pendingMediaId = null;
      this._pendingFallbackToFirst = false;

      let initialIndex;

      if (pendingIndex >= 0) {
        initialIndex = pendingIndex;
      } else if (pendingFallbackToFirst) {
        initialIndex = 0;
      } else {
        const requestedInitialIndex = Number(this.dataset.initialMediaIndex);

        initialIndex = Number.isInteger(requestedInitialIndex)
          ? requestedInitialIndex
          : this.gallerySwiper?.realIndex || 0;
      }

      this._goToMediaIndex(initialIndex, false);

      this._setActiveThumbnail(initialIndex);

      /*
       * Swiper readiness is useful for other
       * components, but PDP navigation does NOT
       * wait for this anymore.
       */
      requestAnimationFrame(() => {
        this.dataset.galleryReady = "true";

        this.dispatchEvent(
          new CustomEvent("media-gallery:ready", {
            bubbles: true,
          }),
        );
      });
    });
  }

  connectedCallback() {
    this.handleVariantChange = (event) => {
      const mediaId = event.detail?.variant?.featuredMediaId;

      if (!this.gallerySwiper) {
        if (mediaId == null) {
          this._pendingMediaId = null;
          this._pendingFallbackToFirst = true;
        } else {
          this._pendingMediaId = mediaId;
          this._pendingFallbackToFirst = false;
        }

        return;
      }

      const mediaIndex = mediaId == null ? -1 : this._getMediaIndexById(mediaId);

      this._goToMediaIndex(mediaIndex >= 0 ? mediaIndex : 0);
    };

    this.handleResize = () => {
      requestAnimationFrame(() => {
        this.thumbsSwiper?.update();
        this.gallerySwiper?.update();

        this._updateThumbnailOverflow();
        this._updateThumbnailNavigationState();
      });
    };

    document.addEventListener("product:variant-change", this.handleVariantChange);

    window.addEventListener("resize", this.handleResize);
  }

  disconnectedCallback() {
    document.removeEventListener("product:variant-change", this.handleVariantChange);

    window.removeEventListener("resize", this.handleResize);

    this.thumbnailResizeObserver?.disconnect();
  }

  _appendResolvedLifestyleSlides() {
    const slidesTemplate = this.querySelector("[data-deferred-lifestyle-media]");

    const thumbsTemplate = this.querySelector("[data-deferred-lifestyle-thumbnails]");

    const slideElements = slidesTemplate ? Array.from(slidesTemplate.content.cloneNode(true).children) : [];

    const thumbElements = thumbsTemplate ? Array.from(thumbsTemplate.content.cloneNode(true).children) : [];

    if (slideElements.length) {
      this._appendSlidesToSwiper(this.gallerySwiper, slideElements);
    }

    if (thumbElements.length && this.thumbsSwiper) {
      this._appendSlidesToSwiper(this.thumbsSwiper, thumbElements);
    }

    slidesTemplate?.remove();
    thumbsTemplate?.remove();

    this._updateThumbnailOverflow();
    this._updateThumbnailNavigationState();
  }

  _initThumbsCarousel() {
    if (this._isGiftCard) {
      return;
    }
    if (this.thumbsSwiper) return;

    const container = this.querySelector(`.${this._prefix}__thumbnails-container`);

    if (!container) return;

    const { Swiper, Thumbs, Mousewheel } = this.swiperLib;

    this.thumbsSwiper = new Swiper(container, {
      modules: [Thumbs, Mousewheel],
      slidesPerView: "auto",
      cssMode: true,
      direction: "vertical",
      watchSlidesProgress: true,
      mousewheel: true,

      on: {
        init: () => {
          requestAnimationFrame(() => {
            this._updateThumbnailOverflow();
            this._updateThumbnailNavigationState();
          });
        },

        update: () => {
          requestAnimationFrame(() => {
            this._updateThumbnailOverflow();
            this._updateThumbnailNavigationState();
          });
        },
      },
    });

    this._initThumbnailArrowNavigation();

    container.addEventListener("scroll", () => {
      requestAnimationFrame(() => {
        this._updateThumbnailNavigationState();
      });
    });

    this.thumbnailResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this._updateThumbnailOverflow();
        this._updateThumbnailNavigationState();
      });
    });

    this.thumbnailResizeObserver.observe(container);

    const slidesWrapper = container.querySelector(".swiper-wrapper");

    if (slidesWrapper) {
      this.thumbnailResizeObserver.observe(slidesWrapper);
    }

    requestAnimationFrame(() => {
      this.thumbsSwiper.update();
      this._updateThumbnailOverflow();
      this._updateThumbnailNavigationState();
    });
  }

  _initThumbnailArrowNavigation() {
    const prevButton = this.querySelector("[data-thumb-navigation-previous]");

    const nextButton = this.querySelector("[data-thumb-navigation-next]");

    if (!prevButton || !nextButton) return;

    prevButton.addEventListener("click", (event) => {
      event.preventDefault();

      const previousIndex = Math.max(this._thumbnailNavIndex - 1, 0);

      this._goToMediaIndex(previousIndex);
    });

    nextButton.addEventListener("click", (event) => {
      event.preventDefault();

      const nextIndex = Math.min(this._thumbnailNavIndex + 1, this._getGalleryLastIndex());

      this._goToMediaIndex(nextIndex);
    });
  }

  _waitForMediaElement(media) {
    return new Promise((resolve) => {
      if (!media) {
        resolve();
        return;
      }

      let settled = false;
      let timeoutId;

      const finish = async () => {
        if (settled) return;
        settled = true;

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        cleanup();

        if (media.tagName === "IMG" && typeof media.decode === "function") {
          try {
            await media.decode();
          } catch (_) {
            // A decode failure should not block the remaining gallery.
          }
        }

        resolve();
      };

      const cleanup = () => {
        media.removeEventListener("load", finish);
        media.removeEventListener("loadeddata", finish);
        media.removeEventListener("error", finish);
      };

      const isVideo = media.tagName === "VIDEO";
      const isReady = isVideo ? media.readyState >= 2 : media.complete && media.naturalWidth > 0;

      if (isReady) {
        finish();
        return;
      }

      media.addEventListener(isVideo ? "loadeddata" : "load", finish, { once: true });
      media.addEventListener("error", finish, { once: true });

      // One failed CDN request must not block every later media pair.
      timeoutId = window.setTimeout(finish, 5000);
    });
  }

  _initCarousel() {
    if (this._isGiftCard) {
      return;
    }
    if (this.gallerySwiper) return;

    const container = this.querySelector(`.${this._prefix}__media-container`);

    if (!container) return;

    const { Swiper, Pagination, Navigation } = this.swiperLib;

    const slideCount = container.querySelectorAll(`.${this._prefix}__media-slides > .swiper-slide`).length;

    const isSingleSlide = slideCount <= 1;
    const disableNavigation = this._isGiftCard || isSingleSlide;

    const nextEl = this.querySelector("[data-gallery-navigation-next]");

    const prevEl = this.querySelector("[data-gallery-navigation-previous]");

    const modules = [];

    if (!this._isGiftCard) {
      modules.push(Pagination, Navigation);
    }

    this.gallerySwiper = new Swiper(container, {
      modules,

      slidesPerView: this._isGiftCard ? 1 : 1.1,
      slidesPerGroup: 1,
      loop: !disableNavigation,

      breakpoints: this._isGiftCard
        ? undefined
        : {
            1024: {
              slidesPerView: 1.3,
            },
            1440: {
              slidesPerView: 1.5,
            },
          },

      autoHeight: this._isGiftCard,

      centeredSlides: this._isGiftCard,
      centerInsufficientSlides: true,

      speed: 400,
      watchSlidesProgress: true,

      navigation:
        !this._isGiftCard && nextEl && prevEl
          ? {
              nextEl,
              prevEl,
            }
          : undefined,

      thumbs:
        !this._isGiftCard && this.thumbsSwiper
          ? {
              swiper: this.thumbsSwiper,
            }
          : undefined,

      pagination:
        !this._isGiftCard && this.thumbsSwiper
          ? {
              el: ".swiper-pagination",
              clickable: true,
            }
          : undefined,
    });

    if (disableNavigation) {
      container.classList.add(`${this._prefix}__media-container--single`);

      nextEl?.setAttribute("data-single-slide", "");
      prevEl?.setAttribute("data-single-slide", "");
    } else {
      container.classList.remove(`${this._prefix}__media-container--single`);

      nextEl?.removeAttribute("data-single-slide");
      prevEl?.removeAttribute("data-single-slide");
    }

    const syncActiveThumbnail = () => {
      if (this._isSyncing || !this.thumbsSwiper) return;

      const index = this.gallerySwiper.realIndex || 0;
      const lastIndex = this._getGalleryLastIndex();

      this._thumbnailNavIndex = index;

      this._setActiveThumbnail(index);

      this._syncThumbnailToIndex(index, index !== 0 && index !== lastIndex);
    };

    this.gallerySwiper.on("slideChange", () => {
      syncActiveThumbnail();

      const visibleSlides = this.gallerySwiper.slides.filter((slide) =>
        slide.classList.contains("swiper-slide-visible"),
      );

      this._selectVideo(visibleSlides);
    });

    if (!this._isGiftCard) {
      this.gallerySwiper.on("realIndexChange", syncActiveThumbnail);

      this.gallerySwiper.on("transitionEnd", () => {
        const index = this.gallerySwiper.realIndex || 0;

        this._setActiveThumbnail(index);
      });
    }
  }

  _syncCarousels() {
    if (!this.gallerySwiper || !this.thumbsSwiper) return;

    this.thumbsSwiper.on("click", () => {
      const clickedIndex = this.thumbsSwiper.clickedIndex;

      if (clickedIndex === undefined || clickedIndex === null) {
        return;
      }

      this._goToMediaIndex(clickedIndex);
    });
  }

  appendLifestyleSlides(colorType) {
    if (this.dataset.lifestyleColorType) return;

    this._appendLifestyleSlidesNow(colorType);
  }

  _appendLifestyleSlidesNow(colorType) {
    if (this.dataset.lifestyleColorType) return;

    const slidesTemplate = this.querySelector(`[data-lifestyle-slides-template="${colorType}"]`);

    if (!slidesTemplate) return;

    this.dataset.lifestyleColorType = colorType;

    const thumbsTemplate = this.querySelector(`[data-lifestyle-thumbs-template="${colorType}"]`);

    const slideElements = Array.from(slidesTemplate.content.cloneNode(true).children);

    const thumbElements = thumbsTemplate ? Array.from(thumbsTemplate.content.cloneNode(true).children) : [];

    if (this.gallerySwiper) {
      this._appendSlidesToSwiper(this.gallerySwiper, slideElements);
    } else {
      this.querySelector(`.${this._prefix}__media-slides`)?.append(...slideElements);
    }

    if (thumbElements.length) {
      if (this.thumbsSwiper) {
        this._appendSlidesToSwiper(this.thumbsSwiper, thumbElements);
      } else {
        this.querySelector(`.${this._prefix}__thumbnail-slides`)?.append(...thumbElements);
      }
    }

    this._initZoomButtons();
    this._updateThumbnailOverflow();
    this._updateThumbnailNavigationState();
  }

  _appendSlidesToSwiper(swiperInstance, elements) {
    if (!elements.length) return;

    const isLoop = !!swiperInstance.params.loop;

    if (isLoop) swiperInstance.loopDestroy();

    swiperInstance.appendSlide(elements);

    if (isLoop) swiperInstance.loopCreate();

    swiperInstance.update();
  }

  _getMediaIndexById(mediaId) {
    const slidesWrapper = this.querySelector(`.${this._prefix}__media-slides`);

    if (!slidesWrapper) return -1;

    const slide = Array.from(slidesWrapper.children).find(
      (candidate) =>
        candidate.classList.contains("swiper-slide") && String(candidate.dataset.mediaId) === String(mediaId),
    );

    if (!slide) return -1;

    if (this.gallerySwiper?.params?.loop && slide.dataset.swiperSlideIndex !== undefined) {
      return Number(slide.dataset.swiperSlideIndex);
    }

    const slides = Array.from(slidesWrapper.children).filter((candidate) =>
      candidate.classList.contains("swiper-slide"),
    );

    return slides.indexOf(slide);
  }

  _goToMediaIndex(index, animate = true) {
    if (!this.gallerySwiper) return;

    const safeIndex = Math.max(0, Math.min(index, this._getGalleryLastIndex()));

    this._thumbnailNavIndex = safeIndex;
    this._isSyncing = true;

    const requestToken = Symbol("goToMediaIndex");

    this._latestMediaIndexRequest = requestToken;

    if (this.gallerySwiper.params.loop && this.gallerySwiper.slideToLoop) {
      this.gallerySwiper.slideToLoop(safeIndex, animate ? undefined : 0);
    } else {
      this.gallerySwiper.slideTo(safeIndex, animate ? undefined : 0);
    }

    if (this.thumbsSwiper) {
      this._setActiveThumbnail(safeIndex);
      this._syncThumbnailToIndex(safeIndex, animate);
    }

    requestAnimationFrame(() => {
      if (this._latestMediaIndexRequest !== requestToken) return;

      if (this.thumbsSwiper) {
        this._setActiveThumbnail(this.gallerySwiper.realIndex || safeIndex);

        this._updateThumbnailNavigationState();
      }

      this._isSyncing = false;
    });
  }

  _setActiveThumbnail(index) {
    if (!this.thumbsSwiper) return;

    const slides = Array.from(this.thumbsSwiper.slides || []);

    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle("swiper-slide-thumb-active", slideIndex === index);
    });
  }

  _syncThumbnailToIndex(index, animate = true) {
    if (!this.thumbsSwiper) return;

    this._thumbnailNavIndex = index;

    const lastIndex = this._getGalleryLastIndex();

    if (index === 0) {
      this.thumbsSwiper.slideTo(0, 0);

      this.thumbsSwiper.el.scrollTo({
        top: 0,
        behavior: "auto",
      });

      requestAnimationFrame(() => {
        this.thumbsSwiper.el.scrollTop = 0;
        this.thumbsSwiper.update();

        this._setActiveThumbnail(index);
        this._updateThumbnailNavigationState();
      });

      return;
    }

    this.thumbsSwiper.slideTo(index, animate ? undefined : 0);

    if (index === lastIndex) {
      this.thumbsSwiper.el.scrollTo({
        top: this.thumbsSwiper.el.scrollHeight,
        behavior: animate ? "smooth" : "auto",
      });
    } else {
      this._scrollThumbnailIntoView(index, animate);
    }

    requestAnimationFrame(() => {
      this._setActiveThumbnail(index);
      this._updateThumbnailNavigationState();
    });
  }

  _scrollThumbnailIntoView(index, animate = true) {
    const container = this.thumbsSwiper?.el;
    const slide = this.thumbsSwiper?.slides?.[index];

    if (!container || !slide) return;

    const offset = index > 0 ? 24 : 0;

    const slideTop = slide.offsetTop - offset;
    const slideBottom = slide.offsetTop + slide.offsetHeight;

    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    if (slideTop < viewTop) {
      container.scrollTo({
        top: slideTop,
        behavior: animate ? "smooth" : "auto",
      });
    } else if (slideBottom > viewBottom) {
      container.scrollTo({
        top: slideBottom - container.clientHeight,
        behavior: animate ? "smooth" : "auto",
      });
    }
  }

  _getGalleryLastIndex() {
    const slides = this.gallerySwiper?.slides || [];

    return Math.max(slides.length - 1, 0);
  }

  _selectVideo(elements) {
    this.activeVideos.forEach((video) => {
      video.pause();
    });

    this.activeVideos = [];

    elements.forEach((element) => {
      const video = element.querySelector("video");

      if (!video) return;

      this.activeVideos.push(video);

      video.play().catch(() => {});
    });
  }

  _updateThumbnailNavigationState() {
    const wrapper = this.querySelector(`.${this._prefix}__thumbnails-wrapper`);

    const prevButton = this.querySelector("[data-thumb-navigation-previous]");

    const nextButton = this.querySelector("[data-thumb-navigation-next]");

    if (!wrapper || !this.thumbsSwiper) return;

    const hasOverflow = wrapper.classList.contains("has-thumbnail-overflow");

    if (!hasOverflow) {
      wrapper.classList.remove("has-top-navigation");

      prevButton?.classList.add("swiper-button-disabled");

      nextButton?.classList.add("swiper-button-disabled");

      return;
    }

    const currentIndex = this._thumbnailNavIndex;
    const lastIndex = this._getGalleryLastIndex();

    const isAtBeginning = currentIndex <= 0;
    const isAtEnd = currentIndex >= lastIndex;

    wrapper.classList.toggle("has-top-navigation", !isAtBeginning);

    prevButton?.classList.toggle("swiper-button-disabled", isAtBeginning);

    nextButton?.classList.toggle("swiper-button-disabled", isAtEnd);
  }

  _updateThumbnailOverflow() {
    const wrapper = this.querySelector(`.${this._prefix}__thumbnails-wrapper`);

    const container = this.querySelector(`.${this._prefix}__thumbnails-container`);

    if (!wrapper || !container) return;

    const slides = Array.from(container.querySelectorAll(`.${this._prefix}__thumbnail-slides > .swiper-slide`));

    const visibleSlides = slides.filter((slide) => {
      return window.getComputedStyle(slide).display !== "none";
    });

    if (!visibleSlides.length) {
      wrapper.classList.remove("has-thumbnail-overflow", "has-top-navigation");

      return;
    }

    const firstSlide = visibleSlides[0];
    const lastSlide = visibleSlides[visibleSlides.length - 1];

    const contentHeight = lastSlide.offsetTop + lastSlide.offsetHeight - firstSlide.offsetTop;

    const hasOverflow = contentHeight > container.clientHeight + 1;

    wrapper.classList.toggle("has-thumbnail-overflow", hasOverflow);

    if (!hasOverflow) {
      wrapper.classList.remove("has-top-navigation");
      container.scrollTop = 0;
    }

    this._updateThumbnailNavigationState();
  }

  _openZoom(mediaId) {
    const template = this._zoomMarkup || document.querySelector("[data-zoom-markup]");

    if (!template) return;

    const content = template.content.cloneNode(true);
    const zoomGallery = content.querySelector("zoom-gallery");

    if (!zoomGallery) return;

    if (mediaId != null) {
      zoomGallery.dataset.preselectMediaId = mediaId;
    }

    const wrapper = document.createElement("div");

    wrapper.appendChild(content);

    this.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: {
          html: wrapper.innerHTML,
          target: "zoom-modal",
        },
      }),
    );
  }

  _initZoomButtons() {
    this.querySelectorAll("[data-open-zoom-media-id]").forEach((button) => {
      if (button.dataset.zoomInitialized === "true") return;

      button.dataset.zoomInitialized = "true";

      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        this._openZoom(button.dataset.openZoomMediaId);
      });
    });
  }

  get _zoomMarkup() {
    return this.querySelector("[data-zoom-markup]");
  }

  get _prefix() {
    return this.dataset.classPrefix;
  }

  get _isMobile() {
    return window.matchMedia("(max-width: 959px)").matches;
  }

  get _isGiftCard() {
    return this.dataset.giftCard === "true";
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
